import { Component, inject, Input, OnInit } from '@angular/core';
import { LocaleService } from '../../../services/locale.service';
import { ShareParams, ShareType, shareTypeToConfig, SocialButtonConfig } from '../../../models/social';
import { Meta } from '@angular/platform-browser';
import { Util } from '../../../util';
import { ToastService } from '../../../_core/services/toast.service';
import { AnalyticsService } from '../../../_core/analytics/analytics.service';

@Component({
  selector: 'app-share-menu',
  templateUrl: './share-menu.component.html',
  styleUrls: ['./share-menu.component.scss'],
})
export class ShareMenuComponent implements OnInit {
  private readonly toastService = inject(ToastService);
  private readonly analyticsService = inject(AnalyticsService);

  private static readonly POPUP_WIDTH = 600;
  private static readonly POPUP_HEIGHT = 600;

  /**
   * Determines which social links will appear
   */
  @Input() include!: ShareType[];
  /**
   * Sets the title of the shared link
   */
  @Input() title: string;
  /**
   * Sets the description of the shared link
   */
  @Input() description: string;
  /**
   * Sets the url of the shared link
   */
  @Input() url: string;

  shareConfigurations!: SocialButtonConfig[];
  textDirection!: string;

  constructor(
    private readonly localeService: LocaleService,
    private readonly meta: Meta
  ) {}

  ngOnInit() {
    this.shareConfigurations = this.include.map((type) => shareTypeToConfig.get(type));
    this.textDirection = this.localeService.getTextDirection();
  }

  copyLink() {
    Util.copyToClipboard(this.url ? this.url : window.location.href);
    this.toastService.showToast('messages.copied', 'info');
    this.analyticsService.sendUserActionEvent('share_tdp', true);
  }

  share(shareConfig: SocialButtonConfig) {
    const params = this.buildCompleteParams();
    const finalUrl = shareConfig.shareLinks.generic + this.serializeParams(shareConfig, params);
    window.open(finalUrl, '_blank', 'width=undefined, height=undefined, noopener');
    this.analyticsService.sendUserActionEvent('share_tdp', true);
  }

  private buildCompleteParams(): ShareParams {
    return {
      url: this.getUrl(),
      title: this.getTitle(),
      description: this.getDescription(),
      image: this.getMetaTagContent('og:image'),
      appId: this.getMetaTagContent('fb:app_id'),
      redirectUrl: this.url,
    };
  }

  private serializeParams(shareConfig: SocialButtonConfig, shareParams: ShareParams): string {
    const searchParams = new URLSearchParams();

    Object.entries(shareConfig.paramKeys).forEach(([key, value]) => {
      if (key === 'description') {
        value = this.getSerializeDescription(shareConfig, shareParams);
      }

      searchParams.append(value, shareParams[key]);
    });

    return searchParams.toString();
  }

  private getUrl(): string {
    return this.url ? this.url : window.location.href;
  }

  private getTitle(): string {
    return this.title ? this.title : this.getMetaTagContent('og:title');
  }

  private getDescription(): string {
    return this.description ? this.description : this.getMetaTagContent('og:description');
  }

  private getMetaTagContent(key: string): string {
    const propMetaTag = this.meta.getTag(`property="${key}"`);
    const nameMetaTag = this.meta.getTag(`name="${key}"`);

    if (propMetaTag) {
      return propMetaTag.getAttribute('content');
    }

    if (nameMetaTag) {
      return nameMetaTag.getAttribute('content');
    }

    return '';
  }

  private getSerializeDescription(shareConfig: SocialButtonConfig, { description, url }: ShareParams): string {
    if ([ShareType.WHATSAPP, ShareType.EMAIL].includes(shareConfig.type)) {
      return description ? `${description}\r\n${url}` : url;
    }

    return description;
  }
}
